import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchWorkflowArtifacts } from '@/services/logicAppsApi';
import { extractWorkflowName } from '@/types/logicApps';
import type { WorkflowDefinitionFiles } from '@/types/logicApps';
import { useWorkflows } from './useWorkflows';

/**
 * A workflow in the same Logic App that can be invoked by another workflow.
 * Mirrors the upstream LogicAppsUX `ChildWorkflowService.getWorkflowsWithRequestTrigger`
 * shape: only workflows with a single Request/Http trigger qualify.
 */
export type CallableWorkflow = {
  /** Short workflow name (e.g., "NestedWorkflow"), suitable for `host.workflow.id`. */
  name: string;
  /** Workflow kind, when known. */
  kind?: 'Stateful' | 'Stateless' | 'Agent';
  /** Name of the (single) Request trigger. */
  triggerName: string;
  /** Trigger HTTP method (lowercased), or undefined when not specified. */
  triggerMethod?: string;
  /** Trigger relative path template, when set. */
  triggerRelativePath?: string;
};

type UseCallableWorkflowsResult = {
  data: CallableWorkflow[];
  isLoading: boolean;
  isError: boolean;
};

type SingleRequestTrigger = {
  triggerName: string;
  inputs: Record<string, unknown>;
};

/**
 * Returns the single Request/Http trigger for the workflow, or null if the
 * workflow's shape doesn't match (multiple triggers, non-Request, etc.) — the
 * only shape that can be invoked by the `Workflow` action's
 * `host.workflow.id` field.
 */
const getSingleRequestTrigger = (
  files: WorkflowDefinitionFiles | undefined,
): SingleRequestTrigger | null => {
  if (!files) return null;
  const workflowJson = files['workflow.json'];
  const triggers = (workflowJson?.definition as Record<string, unknown> | undefined)
    ?.triggers as
    | Record<string, { type?: unknown; kind?: unknown; inputs?: unknown }>
    | undefined;
  if (!triggers) return null;

  const triggerNames = Object.keys(triggers);
  if (triggerNames.length !== 1) return null;

  const onlyTrigger = triggers[triggerNames[0]!]!;
  const type = String(onlyTrigger.type ?? '').toLowerCase();
  const kind = String(onlyTrigger.kind ?? '').toLowerCase();
  if (type !== 'request' || kind !== 'http') return null;

  return {
    triggerName: triggerNames[0]!,
    inputs: (onlyTrigger.inputs as Record<string, unknown>) ?? {},
  };
};

/**
 * Returns a user-facing message describing why a callable workflow can't
 * actually be invoked via the `Workflow` action, or `null` when it's fine.
 *
 * Mirrors the runtime guard in
 * `src/flow/Roles/Flow.Worker.Common/Jobs/Actions/WorkflowActionJob.cs`
 * (`ValidateRelativePath`): a child trigger may use a `relativePath` only
 * when its method is POST. Anything else (GET / PUT / "Default" / unset)
 * combined with the `relativePath` property being *set* (including the
 * empty string — runtime's `TryGetProperty<string>` returns `""`, which
 * is not null, so the guard still fires) makes the runtime reject the
 * invocation.
 *
 * Surfaced as a design-time validation in the picker so authors don't
 * discover the failure on Run.
 */
export const getCallableWorkflowInvocationIssue = (
  workflow: CallableWorkflow,
): string | null => {
  // Property presence — not truthiness — mirrors the runtime contract.
  // `relativePath: ""` triggers the runtime guard, so the FE must treat
  // it the same way.
  const hasRelativePath = workflow.triggerRelativePath !== undefined;
  const isPost = (workflow.triggerMethod ?? '').toLowerCase() === 'post';
  if (hasRelativePath && !isPost) {
    return `This workflow's trigger has a relative path field. Set its method to POST or remove the relative path before invoking it.`;
  }
  return null;
};

/**
 * Lists workflows in the current Logic App that the `Workflow` action can
 * invoke — i.e., other workflows (not the current one) with a single
 * Request/Http trigger.
 *
 * Mirrors `ChildWorkflowService.getWorkflowsWithRequestTrigger` from
 * Azure/LogicAppsUX (apps/Standalone/.../Services/ChildWorkflow.ts).
 */
export const useCallableWorkflows = (
  currentWorkflowName: string | undefined,
): UseCallableWorkflowsResult => {
  const { getOttoToken } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();
  const { data: workflows = [], isLoading: isListLoading, isError: isListError } = useWorkflows();

  const currentNameLower = (currentWorkflowName ?? '').toLowerCase();

  // Candidate workflows = everything except the current one. We still fetch
  // their definitions to filter by trigger kind.
  const candidates = useMemo(
    () =>
      workflows.filter(
        (w) => extractWorkflowName(w.name).toLowerCase() !== currentNameLower,
      ),
    [workflows, currentNameLower],
  );

  const definitionQueries = useQueries({
    queries: candidates.map((workflow) => {
      const shortName = extractWorkflowName(workflow.name);
      return {
        queryKey: [
          'workflowDefinition',
          selectedProject?.toLowerCase(),
          selectedApp?.name?.toLowerCase(),
          shortName.toLowerCase(),
        ],
        queryFn: async () => {
          if (!selectedProject || !selectedApp) return null;
          const { files } = await fetchWorkflowArtifacts({
            workflowName: shortName,
            projectName: selectedProject,
            appName: selectedApp.name ?? '',
            getToken: getOttoToken,
            includeDraft: false,
          });
          return { workflow, files };
        },
        enabled: !!selectedProject && !!selectedApp,
        staleTime: 5 * 60 * 1000,
        // Publish and the workflow list's Refresh invalidate inactive entries.
        // Override QueryProvider's refetchOnMount:false so reopening the picker
        // consumes that invalidation. Fresh definitions still use the cache.
        refetchOnMount: true,
      };
    }),
  });

  // Cached definitions remain available during refetch; hide stale choices
  // until revalidation settles, just as we do for the initial load.
  const isLoading = isListLoading || definitionQueries.some((q) => q.isLoading || q.isFetching);
  // Surface errors from the workflow list fetch as well — without this,
  // a failed list query falls back to `[]` and consumers would see the
  // "no callable workflows" empty state instead of an error.
  const isError = isListError || definitionQueries.some((q) => q.isError);

  const data = useMemo<CallableWorkflow[]>(() => {
    const callable: CallableWorkflow[] = [];
    for (const query of definitionQueries) {
      const result = query.data;
      if (!result) continue;
      const trigger = getSingleRequestTrigger(result.files);
      if (!trigger) continue;
      const method = trigger.inputs['method'];
      const relativePath = trigger.inputs['relativePath'];
      callable.push({
        name: extractWorkflowName(result.workflow.name),
        triggerName: trigger.triggerName,
        ...(result.workflow.kind ? { kind: result.workflow.kind } : {}),
        ...(typeof method === 'string' && method
          ? { triggerMethod: method }
          : {}),
        // Capture the `relativePath` property whenever it's a string, even
        // when empty — the runtime treats `""` as "set" and rejects the
        // invocation on a non-POST method, so the FE must mirror that.
        ...(typeof relativePath === 'string'
          ? { triggerRelativePath: relativePath }
          : {}),
      });
    }
    callable.sort((a, b) => a.name.localeCompare(b.name));
    return callable;
  }, [definitionQueries]);

  return { data, isLoading, isError };
};
